import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLevelBrowserPolicies } from "@/hooks/useLevelBrowserPolicies";

interface LevelBrowserSettingsContentProps {
  levelId: string;
}

export const LevelBrowserSettingsContent = ({ levelId }: LevelBrowserSettingsContentProps) => {
  const { browserPolicy, isLoading, updateBrowserPolicy } = useLevelBrowserPolicies(levelId);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const allowedCategories = browserPolicy?.categories || [];
  const allowedDomains = browserPolicy?.allowlist || [];
  const blockedDomains = browserPolicy?.blocklist || [];
  const blockVideos = browserPolicy?.blockVideos || false;
  const accuracy = browserPolicy?.accuracy;

  // Convert numeric accuracy to string representation
  const accuracyString = accuracy === 1 ? "fastest" : accuracy === 3 ? "accurate" : "balanced";

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Browser Settings Overview</h3>
        <p className="text-sm text-gray-600">Summary of browser policies for this level</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Allowed Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{allowedCategories.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Whitelisted Domains</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{allowedDomains.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Blacklisted Domains</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{blockedDomains.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Policy Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Policy Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Allowed Categories */}
          <div>
            <h4 className="font-medium mb-2">Allowed Categories</h4>
            {allowedCategories.length === 0 ? (
              <p className="text-sm text-gray-500">No categories explicitly allowed</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {allowedCategories.map((category, index) => (
                  <Badge key={index} variant="success">{category}</Badge>
                ))}
              </div>
            )}
          </div>

          {/* Whitelisted Domains */}
          <div>
            <h4 className="font-medium mb-2">Whitelisted Domains</h4>
            {allowedDomains.length === 0 ? (
              <p className="text-sm text-gray-500">No domains explicitly allowed</p>
            ) : (
              <div className="space-y-1">
                {allowedDomains.slice(0, 5).map((domain, index) => (
                  <div key={index} className="text-sm bg-green-50 px-2 py-1 rounded">{domain}</div>
                ))}
                {allowedDomains.length > 5 && (
                  <p className="text-xs text-gray-500">... and {allowedDomains.length - 5} more</p>
                )}
              </div>
            )}
          </div>

          {/* Blacklisted Domains */}
          <div>
            <h4 className="font-medium mb-2">Blacklisted Domains</h4>
            {blockedDomains.length === 0 ? (
              <p className="text-sm text-gray-500">No domains explicitly blocked</p>
            ) : (
              <div className="space-y-1">
                {blockedDomains.slice(0, 5).map((domain, index) => (
                  <div key={index} className="text-sm bg-red-50 px-2 py-1 rounded">{domain}</div>
                ))}
                {blockedDomains.length > 5 && (
                  <p className="text-xs text-gray-500">... and {blockedDomains.length - 5} more</p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Video Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Video Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <div className="font-medium">Videos</div>
              <p className="text-sm text-muted-foreground">Control access to video content on websites for all devices in this level.</p>
            </div>
            <Select 
              value={blockVideos ? "block" : "allow"} 
              onValueChange={(value) => updateBrowserPolicy({ blockVideos: value === "block" })}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="allow">Allow</SelectItem>
                <SelectItem value="block">Block</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Accuracy Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Content Filtering Accuracy</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <div className="font-medium">Processing Speed</div>
              <p className="text-sm text-muted-foreground">Balance between filtering speed and accuracy for all devices in this level.</p>
            </div>
            <Select 
              value={accuracyString} 
              onValueChange={(value) => {
                // Convert string to numeric: fastest=1, balanced=2, accurate=3
                const numericAccuracy = value === "fastest" ? 1 : value === "accurate" ? 3 : 2;
                updateBrowserPolicy({ accuracy: numericAccuracy });
              }}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fastest">Fastest</SelectItem>
                <SelectItem value="balanced">Balanced</SelectItem>
                <SelectItem value="accurate">Accurate</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <h4 className="font-medium text-blue-900 mb-2">How Browser Policies Work</h4>
          <div className="text-sm text-blue-800 space-y-2">
            <p>• <strong>Categories:</strong> Control access to entire website categories</p>
            <p>• <strong>Whitelist:</strong> Explicitly allow specific domains (overrides category blocks)</p>
            <p>• <strong>Blacklist:</strong> Explicitly block specific domains (overrides category allows)</p>
            <p>• <strong>Priority:</strong> Blacklist &gt; Whitelist &gt; Categories</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};